import React, { useEffect, useState } from 'react';
import { CheckCircle, ArrowRight, Loader2, XCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { useAuth } from '../../../hooks/useAuth';
import { getCurrentSubscription, CurrentSubscription } from '../../../utils/subscriptionApi';
import { isPrerendering } from '../../../utils/prerender';

const SubscriptionSuccessPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<CurrentSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isPrerendering()) {
      // During prerender, skip subscription fetch and just show a generic success shell
      setLoading(false);
      return;
    }

    const fetchSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const currentSub = await getCurrentSubscription();
        
        setSubscription(currentSub);
      } catch (err) {
        console.error('❌ [SubscriptionSuccessPage] Error fetching subscription:', err);
        setError('Failed to load subscription details');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  const handleContinue = () => {
    window.location.href = '/app';
  };

  // Show loading state while auth is being determined
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50">
        <Card className="max-w-md mx-auto text-center p-8">
          <Loader2 className="w-16 h-16 mx-auto text-emerald-600 animate-spin mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Loading...</h1>
          <p className="text-slate-600">
            Please wait while we verify your authentication.
          </p>
        </Card>
      </div>
    );
  }

  // Show sign-in prompt only after auth loading is complete and user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50">
        <Card className="max-w-md mx-auto text-center p-8">
          <div className="text-red-600 mb-4">
            <XCircle className="w-16 h-16 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Please Sign In</h1>
          <p className="text-slate-600 mb-6">
            You need to be signed in to view your subscription details.
          </p>
          <Button onClick={() => window.location.href = '/app'}>
            Sign In
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50">
      <Card className="max-w-md mx-auto text-center p-8">
        {loading ? (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-emerald-600 animate-spin mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Processing...</h1>
            <p className="text-slate-600">
              We're setting up your subscription. This may take a few moments.
            </p>
          </>
        ) : error ? (
          <>
            <div className="text-red-600 mb-4">
              <XCircle className="w-16 h-16 mx-auto" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Something went wrong</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Button onClick={handleContinue} className="w-full">
                Continue to App
              </Button>
              <Button variant="secondary" onClick={() => window.location.reload()} className="w-full">
                Try Again
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-emerald-600 mb-4">
              <CheckCircle className="w-16 h-16 mx-auto" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Welcome to {subscription?.tier}!</h1>
            <p className="text-slate-600 mb-6">
              {subscription?.subscription ? (
                <>
                  Your subscription is now active and ready to use. You now have access to all {subscription.tier} features.
                </>
              ) : (
                'Your subscription is being processed. You should have access to all features shortly.'
              )}
            </p>
            
            {subscription?.subscription && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-emerald-900 mb-2">Subscription Details</h3>
                <div className="space-y-1 text-sm text-emerald-700">
                  <p><strong>Plan:</strong> {subscription.tier} ({subscription.subscription.billingCycle})</p>
                  <p><strong>Status:</strong> {subscription.subscription.status}</p>
                  <p><strong>Next billing:</strong> {
                    subscription.subscription.nextBillingDate 
                      ? subscription.subscription.nextBillingDate.toLocaleDateString()
                      : subscription.subscription.status === 'canceled'
                        ? 'No further billing (cancelled)'
                        : subscription.subscription.cancelAtPeriodEnd 
                          ? 'No further billing (scheduled for cancellation)'
                          : 'Processing...'
                  }</p>
                </div>
              </div>
            )}
            
            <Button onClick={handleContinue} className="w-full">
              <div className="flex items-center justify-center">
                <span>Start Using ThreadIt</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </div>
            </Button>
            
            <p className="text-xs text-slate-500 mt-4">
              You can manage your subscription anytime from your account settings.
            </p>
          </>
        )}
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
